/**
 * Repository Change Event Emitter
 *
 * Emite eventos cuando los repositorios tienen cambios locales.
 * Permite que SyncManager observe los cambios sin depender directamente de los repositorios.
 *
 * Esto rompe la dependencia circular:
 * syncClient -> syncManagerWeb -> registerAdapters -> DexieRepos -> syncClient
 *
 * NOTA: Este emitter NO guarda pending changes. Esa responsabilidad
 * es del SyncManager, que los persiste en la base de datos para
 * sobrevivir a reinicios de la app.
 *
 * TIPOS DE EVENTOS:
 * - repositoryEvents: Para sincronización con Supabase (escuchado por SyncManager)
 * - localRepositoryEvents: Para actualizar componentes UI (NO sincroniza con Supabase)
 */
export type ChangeAction = 'INSERT' | 'UPDATE' | 'DELETE' | 'BULK_INSERT' | 'BULK_DELETE' | 'BULK_UPDATE';

export interface RepositoryChangeEvent {
  table: string;
  action: ChangeAction;
  // Para INSERT/UPDATE: un registro; para DELETE: un id (string);
  // para BULK_INSERT: array de registros; para BULK_DELETE: array de ids
  record: Record<string, unknown> | string | Record<string, unknown>[] | string[];
  timestamp: string;
  localOnly?: boolean;  // Si es true, SyncManager debe ignorar este evento
}

type EventListener = (event: RepositoryChangeEvent) => void;

/**
 * Event emitter singleton para cambios en repositorios (sincronización con Supabase)
 * Solo emite eventos a los listeners - NO persiste cambios
 */
class RepositoryChangeEventEmitter {
  private listeners: Set<EventListener> = new Set();

  /**
   * Emitir un evento de cambio
   * Notifica a todos los listeners (ej: SyncManager)
   */
  emit(table: string, action: ChangeAction, record: Record<string, unknown> | string | Record<string, unknown>[] | string[]): void {
    const event: RepositoryChangeEvent = {
      table,
      action,
      record,
      timestamp: new Date().toISOString()
    };

    // Notificar a todos los listeners
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('[RepositoryChangeEventEmitter] Error in listener:', error);
      }
    });
  }

  /**
   * Suscribirse a eventos de cambio
   * @returns Función para desuscribirse
   */
  subscribe(listener: EventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Desuscribir un listener específico
   */
  unsubscribe(listener: EventListener): void {
    this.listeners.delete(listener);
  }

  /**
   * Eliminar todos los listeners
   */
  clearAllListeners(): void {
    this.listeners.clear();
  }

  /**
   * Cantidad de listeners activos (útil para debugging)
   */
  getListenerCount(): number {
    return this.listeners.size;
  }
}

/**
 * Event emitter singleton para eventos locales SOLAMENTE (actualización de UI)
 * Estos eventos NO se sincronizan con Supabase
 */
class LocalRepositoryChangeEventEmitter {
  private listeners: Set<EventListener> = new Set();

  /**
   * Emitir un evento local (no sincroniza con Supabase)
   */
  emit(table: string, action: ChangeAction, record: Record<string, unknown> | string | Record<string, unknown>[] | string[]): void {
    const event: RepositoryChangeEvent = {
      table,
      action,
      record,
      timestamp: new Date().toISOString(),
      localOnly: true  // Marca como evento local para no sincronizar
    };

    // Notificar a todos los listeners
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('[LocalRepositoryChangeEventEmitter] Error in listener:', error);
      }
    });
  }

  /**
   * Suscribirse a eventos locales
   * @returns Función para desuscribirse
   */
  subscribe(listener: EventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Cantidad de listeners activos
   */
  getListenerCount(): number {
    return this.listeners.size;
  }
}

// Singleton exports
export const repositoryEvents = new RepositoryChangeEventEmitter();
export const localRepositoryEvents = new LocalRepositoryChangeEventEmitter();
